import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useNavigate, useLocation } from "react-router";
import { getPhoneRegex } from "helpers/get-phone-regex.helper";
import { register } from "api/client/zenpilates/register";

const schema = Yup.object().shape({
    firstName: Yup.string()
        .required("Imię jest wymagane")
        .max(100, "Wprowadź max. 100 znaków"),
    lastName: Yup.string()
        .required("Nazwisko jest wymagane")
        .max(100, "Wprowadź max. 100 znaków"),
    phone: Yup.string()
        .required("Numer telefonu jest wymagany")
        .matches(getPhoneRegex(), "Niepoprawny nr telefonu")
        .max(100, "Wprowadź max. 100 znaków"),
    password: Yup.string()
        .required("Hasło jest wymagane")
        .min(8, "Hasło musi zawierać co najmniej 8 znaków")
        .matches(/[a-z]/, "Hasło musi zawierać co najmniej jedną małą literę")
        .matches(/[A-Z]/, "Hasło musi zawierać co najmniej jedną wielką literę")
        .matches(/\d/, "Hasło musi zawierać co najmniej jedną cyfrę")
        .matches(/[!@#$%^&*]/, "Hasło musi zawierać co najmniej jeden znak specjalny (!@#$%^&*)"),
    confirmPassword: Yup.string()
        .required("Powtórz hasło")
        .oneOf([Yup.ref("password")], "Hasła muszą być takie same"),
    agreementAccepted: Yup.boolean()
        .oneOf([true], "Musisz zaakceptować regulamin")
        .required("Musisz zaakceptować regulamin"),
});

export type FormValues = Yup.InferType<typeof schema>;

export const useRegister = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {
        handleSubmit,
        formState: { errors, isValid },
        control,
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            agreementAccepted: false,
        },
    });

    const { mutate, isPending, error } = useMutation({
        mutationFn: (data: FormValues) => register(data),
        onSuccess: (data) => {
            navigate("/login", {
                state: {
                    login: data.data.data.login,
                    redirectTo: location.state?.redirectTo,
                }
            });
        },
        onError: (err: any) => {
            let message = "Wystąpił niespodziewany błąd.";
            if (isAxiosError(err)) {
                message = err.response?.data?.message || err.message || message;
            } else if (err instanceof Error) {
                message = err.message;
            }
            console.log(`Sign in error: ${message}`);
        },
    });

    const onSubmit = handleSubmit((data) => {
        mutate(data);
    });

    return {
        onSubmit,
        loading: isPending,
        error: (error as any)?.response?.data?.message || (error as any)?.message,
        control,
        isValid,
        formErrors: errors,
    };
};
